/**
 * RuleScheduler.js - Calendar-based screen time rule scheduler
 *
 * Polls Firestore every 60 seconds, checks current time vs active rules,
 * and triggers app pinning start/stop via the Capacitor ScreenTime plugin.
 *
 * Firestore schema:
 * families/{familyId}/screenTimeRules/{ruleId}: {
 *   type: 'chore' | 'calendar_event' | 'manual_lock',
 *   childId: string,
 *   startTime: timestamp,
 *   endTime: timestamp,
 *   recurring: boolean,
 *   recurringDays: ['mon','tue','wed','thu','fri'],
 *   screenTimeEnabled: boolean,
 *   unlockOnApprove: boolean,
 *   createdBy: 'parent' | 'system',
 *   createdAt: timestamp
 * }
 */

import { collection, query, where, getDocs, deleteDoc, doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Capacitor } from '@capacitor/core';

const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

class RuleScheduler {
  constructor() {
    this.familyId = null;
    this.childId = null;
    this.pollInterval = null;
    this.unsubscribeFirestore = null;
    this.isPinned = false;
    this.onStateChange = null; // callback(isPinned, activeRules)
    this.masterEnabled = true;
    this._plugin = null;
  }

  /**
   * Initialize the scheduler for a specific family/child
   */
  init({ familyId, childId, onStateChange }) {
    this.familyId = familyId;
    this.childId = childId;
    this.onStateChange = onStateChange;
    this._plugin = this._getPlugin();

    // Start real-time listener + polling
    this._startFirestoreListener();
    this._startPolling();

    console.log('[RuleScheduler] Initialized for family:', familyId, 'child:', childId);
  }

  /**
   * Stop all listeners and polling
   */
  destroy() {
    if (this.unsubscribeFirestore) {
      this.unsubscribeFirestore();
      this.unsubscribeFirestore = null;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    console.log('[RuleScheduler] Destroyed');
  }

  /**
   * Manually enable/disable all rules (master toggle)
   */
  setMasterEnabled(enabled) {
    this.masterEnabled = enabled;
    if (!enabled && this.isPinned) {
      this._stopPinning();
    }
    console.log('[RuleScheduler] Master enabled:', enabled);
  }

  /**
   * Get Capacitor plugin (Android only)
   */
  _getPlugin() {
    if (!Capacitor.isNativePlatform()) return null;
    try {
      const { Plugins } = require('@capacitor/core');
      return Plugins.ScreenTime || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Start real-time Firestore listener for immediate rule changes
   */
  _startFirestoreListener() {
    if (!this.familyId) return;

    const rulesRef = collection(db, `families/${this.familyId}/screenTimeRules`);
    const q = query(
      rulesRef,
      where('childId', '==', this.childId || ''),
      where('screenTimeEnabled', '==', true)
    );

    this.unsubscribeFirestore = onSnapshot(q, (snapshot) => {
      console.log('[RuleScheduler] Firestore rules updated, count:', snapshot.docs.length);
      this._evaluateRules(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('[RuleScheduler] Firestore listener error:', err);
    });
  }

  /**
   * Start polling every 60 seconds as backup to real-time listener
   */
  _startPolling() {
    // Run immediately on init
    this._checkRules();

    // Then every 60 seconds
    this.pollInterval = setInterval(() => {
      this._checkRules();
    }, 60 * 1000);
  }

  /**
   * Fetch and evaluate rules from Firestore (poll-based)
   */
  async _checkRules() {
    if (!this.familyId || !this.childId) return;
    if (!this.masterEnabled) return;

    try {
      const rulesRef = collection(db, `families/${this.familyId}/screenTimeRules`);
      const q = query(
        rulesRef,
        where('childId', '==', this.childId),
        where('screenTimeEnabled', '==', true)
      );
      const snapshot = await getDocs(q);
      const rules = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      this._evaluateRules(rules);
    } catch (err) {
      console.error('[RuleScheduler] Poll error:', err);
    }
  }

  /**
   * Resolve conflicts between overlapping rules.
   * Priority: manual_lock > chore > calendar_event
   * If any high-priority rule is active, it takes precedence.
   */
  _resolveConflicts(activeRules) {
    if (activeRules.length <= 1) return activeRules;
    
    const priority = { manual_lock: 3, chore: 2, calendar_event: 1 };
    const maxPriority = Math.max(...activeRules.map(r => priority[r.type] || 0));
    const highPriorityRules = activeRules.filter(r => (priority[r.type] || 0) === maxPriority);
    
    if (highPriorityRules.length < activeRules.length) {
      console.log('[RuleScheduler] Conflict resolved: using', highPriorityRules.map(r => r.type));
    }
    
    return highPriorityRules;
  }

  /**
   * Core logic: given a list of rules, determine if we should be pinned right now
   */
  _evaluateRules(rules) {
    if (!this.masterEnabled) {
      if (this.isPinned) this._stopPinning();
      return;
    }

    const now = new Date();
    const expiredRules = [];
    let shouldBePinned = false;
    const activeRules = [];

    for (const rule of rules) {
      const startTime = rule.startTime?.toDate?.() || (rule.startTime ? new Date(rule.startTime) : null);
      const endTime = rule.endTime?.toDate?.() || (rule.endTime ? new Date(rule.endTime) : null);

      // Check if rule is currently active
      let isActive = false;

      if (rule.recurring && rule.recurringDays?.length > 0) {
        // Recurring rule: check if today is a matching day and time is in range
        const todayName = DAY_NAMES[now.getDay()];
        if (rule.recurringDays.includes(todayName)) {
          if (startTime && endTime) {
            const todayStart = this._todayAt(startTime);
            const todayEnd = this._todayAt(endTime);
            isActive = now >= todayStart && now <= todayEnd;
          }
        }
      } else if (startTime && endTime) {
        // One-time rule
        isActive = now >= startTime && now <= endTime;

        // Mark expired one-time rules for cleanup
        if (now > endTime && rule.type !== 'chore') {
          expiredRules.push(rule.id);
        }
      } else if (startTime && !endTime) {
        // Indefinite rule (manual lock with no end time)
        isActive = now >= startTime;
      }

      if (isActive) {
        shouldBePinned = true;
        activeRules.push(rule);
      }
    }

    // Clean up expired rules
    expiredRules.forEach(ruleId => this._deleteExpiredRule(ruleId));

    // Resolve conflicts if multiple rules are active
    const resolvedRules = this._resolveConflicts(activeRules);

    // Update pin state if changed
    if (shouldBePinned && !this.isPinned) {
      this._startPinning(resolvedRules);
    } else if (!shouldBePinned && this.isPinned) {
      this._stopPinning();
    }

    // Notify state change listeners
    if (this.onStateChange) {
      const nextUnlock = this._getNextUnlockTime(rules);
      this.onStateChange(this.isPinned, resolvedRules, nextUnlock);
    }
  }

  /**
   * Create a Date for "today at the same HH:MM:SS as the given date"
   */
  _todayAt(date) {
    const today = new Date();
    today.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return today;
  }

  /**
   * Find the next time the device will unlock
   */
  _getNextUnlockTime(rules) {
    const now = new Date();
    let earliest = null;

    for (const rule of rules) {
      const endTime = rule.endTime?.toDate?.() || (rule.endTime ? new Date(rule.endTime) : null);
      if (endTime && endTime > now) {
        if (!earliest || endTime < earliest) {
          earliest = endTime;
        }
      }
    }

    return earliest;
  }

  /**
   * Delete an expired rule from Firestore
   */
  async _deleteExpiredRule(ruleId) {
    try {
      await deleteDoc(doc(db, `families/${this.familyId}/screenTimeRules/${ruleId}`));
      console.log('[RuleScheduler] Deleted expired rule:', ruleId);
    } catch (err) {
      console.error('[RuleScheduler] Failed to delete expired rule:', err);
    }
  }

  /**
   * Trigger app pinning via native plugin
   */
  async _startPinning(activeRules) {
    this.isPinned = true;
    console.log('[RuleScheduler] Starting pin for rules:', activeRules.map(r => r.type));

    if (this._plugin) {
      try {
        await this._plugin.startPinning();
      } catch (err) {
        console.error('[RuleScheduler] startPinning() error:', err);
      }
    }
  }

  /**
   * Stop app pinning via native plugin
   */
  async _stopPinning() {
    this.isPinned = false;
    console.log('[RuleScheduler] Stopping pin');

    if (this._plugin) {
      try {
        await this._plugin.stopPinning();
      } catch (err) {
        console.error('[RuleScheduler] stopPinning() error:', err);
      }
    }
  }
}

// Singleton instance
const ruleScheduler = new RuleScheduler();
export default ruleScheduler;

/**
 * Helper: create a new screen time rule document
 */
export function buildScreenTimeRule({
  type,
  childId,
  startTime,
  endTime,
  recurring = false,
  recurringDays = [],
  unlockOnApprove = false,
  createdBy = 'parent',
  linkedChoreId = null,
  linkedEventId = null,
}) {
  return {
    type, // 'chore' | 'calendar_event' | 'manual_lock'
    childId,
    startTime: startTime instanceof Date ? Timestamp.fromDate(startTime) : startTime,
    endTime: endTime instanceof Date ? Timestamp.fromDate(endTime) : endTime,
    recurring,
    recurringDays,
    screenTimeEnabled: true,
    unlockOnApprove,
    createdBy,
    createdAt: Timestamp.now(),
    ...(linkedChoreId && { linkedChoreId }),
    ...(linkedEventId && { linkedEventId }),
  };
}
